import React, { useEffect, useState } from 'react';
import { useLocation, Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectCurrentAccessToken,
  selectCognitoUsername,
} from '../../store/slices/auth/authSelectors';
import { refreshSession } from '../../store/slices/auth/authThunks';
import IconIndicator from '../user-feedback/IconIndicator';
import { Container } from 'rsuite';
import { useInternalUrl } from '../../hooks/usePlaceNavigate';
import { useAppDispatch } from '../../store/store';

const RequireAuth = () => {
  const dispatch = useAppDispatch();
  const cognitoUsername = useSelector(selectCognitoUsername);
  const token = useSelector(selectCurrentAccessToken);
  // Wait for redux-persist rehydration before evaluating auth state.
  // _persist is absent in unit tests (no PersistGate) — treat that as already
  // rehydrated so the component proceeds normally.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rehydrated = useSelector((state: any) =>
    state._persist ? state._persist.rehydrated : true
  );
  const location = useLocation();
  const initUrl = useInternalUrl();
  // sessionChecked tracks whether the refreshSession call has completed.
  // Stays false until both rehydration is done AND the session refresh attempt
  // (success or failure) has resolved.
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    // Do not attempt the session refresh until redux-persist has finished
    // rehydrating. Without this guard, refreshSession could complete before
    // rehydration and see token='' (initial state), causing a false redirect.
    if (!rehydrated) {
      return;
    }

    const refresh = async () => {
      // No portal-scoped session in Redux — skip refresh so we don't pick up
      // an Amplify session that belongs to a different portal.
      if (!cognitoUsername) {
        setSessionChecked(true);
        return;
      }
      try {
        await dispatch(refreshSession());
      } catch {
        // Ignore — if fetchAuthSession throws (e.g. no Amplify session in the
        // test environment), the token from redux-persist rehydration is used
        // as the fallback for the render decision below.
      } finally {
        setSessionChecked(true);
      }
    };

    refresh();
    // cognitoUsername intentionally excluded: run once per rehydration, not on
    // every username change (which would cause an infinite loop in some flows).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rehydrated, dispatch]);

  if (!rehydrated || !sessionChecked) {
    return (
      <Container
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          margin: 0,
          padding: 0,
          width: '100vw',
        }}
      >
        <IconIndicator />
      </Container>
    );
  }

  if (!cognitoUsername || !token) {
    return (
      <Navigate
        replace
        state={{ from: location }}
        to={initUrl}
      />
    );
  }

  return <Outlet />;
};

export default RequireAuth;
